import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BadgeCheck,
  BarChart3,
  BrainCircuit,
  CheckCircle2,
  CircleDot,
  Layers3,
  ShieldCheck,
  Sparkles,
  Workflow
} from 'lucide-react';
import Navbar from './Navbar';
import BottomContent from './BottomContent';
import ScrollButton from './ScrollButton';
import ContactButton from './ContactButton';
import DarkModeButton from './DarkModeButton';
import Thank from './Thank';
import './SolutionPageLayout.css';

const iconMap = {
  analytics: BarChart3,
  ai: BrainCircuit,
  trust: ShieldCheck,
  workflow: Workflow,
  layers: Layers3,
  check: BadgeCheck,
};

export default function SolutionPageLayout({
  eyebrow,
  title,
  subtitle,
  description,
  image,
  stats = [],
  highlights = [],
  overviewTitle,
  overviewText,
  overviewPoints = [],
  workflowTitle = 'How It Works',
  workflow = [],
  outcomesTitle = 'Business Outcomes',
  outcomes = [],
}) {
  return (
    <>
      <Navbar />

      <main className="solution-page">
        <section className="solution-hero">
          <div className="solution-hero-bg" />
          <div className="solution-container solution-hero-grid">
            <div className="solution-hero-copy">
              <span className="solution-eyebrow">
                <Sparkles size={17} />
                {eyebrow}
              </span>
              <h1>{title}</h1>
              <h2>{subtitle}</h2>
              <p>{description}</p>
              <div className="solution-actions">
                <Link to="/contact" className="solution-primary-btn">
                  Talk to us
                  <ArrowRight size={18} />
                </Link>
                <Link to="/scheduleDemo" className="solution-secondary-btn">
                  Schedule demo
                </Link>
              </div>
            </div>

            <div className="solution-visual-wrap">
              <img src={image} alt="" className="solution-visual" />
              <div className="solution-floating-card">
                <BrainCircuit size={22} />
                <span>AI-ready delivery</span>
              </div>
            </div>
          </div>
        </section>

        {stats.length > 0 && (
          <section className="solution-stats-section">
            <div className="solution-container solution-stats">
              {stats.map((stat) => (
                <div className="solution-stat" key={stat.label}>
                  <strong>{stat.value}</strong>
                  <span>{stat.label}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="solution-section">
          <div className="solution-container">
            <div className="solution-section-heading">
              <span>Capabilities</span>
              <h2>Built for practical, measurable transformation</h2>
            </div>
            <div className="solution-card-grid">
              {highlights.map((item) => {
                const Icon = iconMap[item.icon] || CircleDot;
                return (
                  <article className="solution-card" key={item.title}>
                    <div className="solution-card-icon">
                      <Icon size={24} />
                    </div>
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="solution-section solution-overview-section">
          <div className="solution-container solution-overview-grid">
            <div className="solution-overview-copy">
              <span className="solution-section-label">Overview</span>
              <h2>{overviewTitle}</h2>
              <p>{overviewText}</p>
            </div>
            <div className="solution-point-panel">
              {overviewPoints.map((point) => (
                <div className="solution-point" key={point}>
                  <CheckCircle2 size={19} />
                  <span>{point}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="solution-section">
          <div className="solution-container">
            <div className="solution-section-heading">
              <span>Delivery Flow</span>
              <h2>{workflowTitle}</h2>
            </div>
            <div className="solution-workflow">
              {workflow.map((step, index) => (
                <article className="solution-step" key={step.title}>
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {outcomes.length > 0 && (
          <section className="solution-section solution-outcomes-section">
            <div className="solution-container solution-outcomes-grid">
              <div>
                <span className="solution-section-label">Outcomes</span>
                <h2>{outcomesTitle}</h2>
              </div>
              <div className="solution-outcomes-list">
                {outcomes.map((outcome) => (
                  <div className="solution-outcome" key={outcome}>
                    <CheckCircle2 size={18} />
                    <span>{outcome}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Thank />
      <BottomContent />
      <DarkModeButton />
      <ContactButton />
      <ScrollButton />
    </>
  );
}
